import { Link } from 'react-router-dom';
import { Facebook, Instagram } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getSiteSettings } from '../services/api.shop';
import { SiteSettings } from '../types';
import { useTranslation } from '../context/TranslationContext';

const DEFAULT_SETTINGS: Pick<SiteSettings, 'contactEmail' | 'facebookUrl' | 'instagramUrl' | 'tikTokUrl' | 'pinterestUrl'> = {
    contactEmail: 'support@petito.be',
    facebookUrl: '',
    instagramUrl: '',
    tikTokUrl: '',
    pinterestUrl: '',
};

function tf(t: (key: string) => string, key: string, fallback: string): string {
    const value = t(key);
    return value === key ? fallback : value;
}

const Footer = () => {
    const { t } = useTranslation();

    const { data: settings } = useQuery({
        queryKey: ['siteSettings', 'footer'],
        queryFn: getSiteSettings,
        staleTime: 10 * 60 * 1000,
    });

    const contactEmail = settings?.contactEmail || DEFAULT_SETTINGS.contactEmail;
    const facebookUrl = settings?.facebookUrl || DEFAULT_SETTINGS.facebookUrl;
    const instagramUrl = settings?.instagramUrl || DEFAULT_SETTINGS.instagramUrl;
    const tikTokUrl = settings?.tikTokUrl || '';
    const pinterestUrl = settings?.pinterestUrl || '';

    return (
        <footer className="mt-auto border-t border-[var(--accent-color)] bg-[var(--bg-color)] py-8 text-[var(--text-color)] shadow-lg max-lg:pb-mobile-nav">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-5">
                    <div>
                        <h3 className="font-bold mb-4">{t('footer.brand')}</h3>
                        <p>{t('footer.tagline')}</p>
                        <p className="mt-2 text-sm opacity-85">
                            {tf(
                                t,
                                'footer.eco',
                                'Circular parenting - every re-use keeps baby gear out of landfill.'
                            )}{' '}
                            <Link to="/about" className="font-medium underline underline-offset-2">
                                {tf(t, 'footer.eco.link', 'About us')}
                            </Link>
                        </p>
                    </div>
                    <div>
                        <h3 className="font-bold mb-4">{t('footer.company')}</h3>
                        <ul className="space-y-2">
                            <li><Link to="/about">{t('footer.aboutus')}</Link></li>
                            {/* Phase 10a: hide thin Blog stub until real posts exist
                            <li><Link to="/blog">{t('footer.blog')}</Link></li>
                            */}
                            <li><Link to="/how-it-works">{tf(t, 'footer.howitworks', 'How it works')}</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-bold mb-4">{t('footer.support')}</h3>
                        <ul className="space-y-2">
                            <li><Link to="/helpdesk">{t('footer.helpandcontact')}</Link></li>
                            <li><Link to="/faq">{t('footer.faq')}</Link></li>
                            <li><Link to="/shipping">{t('footer.shippingandreturns')}</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-bold mb-4">{t('footer.legal')}</h3>
                        <ul className="space-y-2">
                            <li><Link to="/privacy">{t('footer.privacypolicy')}</Link></li>
                            <li><Link to="/terms">{t('footer.termsofservice')}</Link></li>
                            <li><Link to="/rules">{t('footer.communityrules')}</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-bold mb-4">
                            {tf(t, 'footer.partner', 'Partner with us')}
                        </h3>
                        <p className="mb-3 text-sm opacity-90">
                            {tf(t, 'footer.partner.text', "Brands and investors - let's build the circular economy together.")}
                        </p>
                        <Link
                            to="/partner"
                            className="inline-block rounded-lg bg-[var(--accent-color)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                        >
                            {tf(t, 'footer.partner.cta', 'Get in touch')}
                        </Link>
                    </div>
                </div>

                <div className="mt-8 flex flex-col items-center gap-4 border-t border-[var(--accent-color)] pt-6">
                    <div className="flex justify-center gap-4">
                        {facebookUrl && (
                            <a href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                <Facebook className="h-5 w-5 hover:opacity-80" />
                            </a>
                        )}
                        {instagramUrl && (
                            <a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                <Instagram className="h-5 w-5 hover:opacity-80" />
                            </a>
                        )}
                        {tikTokUrl && (
                            <a href={tikTokUrl} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-sm font-semibold hover:opacity-80">
                                TikTok
                            </a>
                        )}
                        {pinterestUrl && (
                            <a href={pinterestUrl} target="_blank" rel="noopener noreferrer" aria-label="Pinterest" className="text-sm font-semibold hover:opacity-80">
                                Pinterest
                            </a>
                        )}
                    </div>
                    {contactEmail && (
                        <a href={`mailto:${contactEmail}`} className="text-sm hover:underline">
                            {contactEmail}
                        </a>
                    )}
                    <p className="text-center text-sm">{'\u00A9'} {new Date().getFullYear()} {t('footer.copyright')}</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;